import countries from "@server/CountriesList";
import { InputField } from "@typings/InputField";
import { LICENCE_VERSIONS } from "@typings/Licence";
import { PartnerInput } from "@typings/Partner";
import {
  isSapAllowedByPolicy,
  LicensingPolicy,
} from "@typings/LicensingPolicy";

class InputFields {
  RESTRICT_TAGS_DESCRIPTION = `Limits this partner's licence managers to the
      <b>Production</b> and <b>NFR</b> tags.<br /><br />
      When off, they may also issue <b>Trial</b> and <b>PoC</b> licences, up to
      the quotas set above.`;

  FIXED_TYPE_ONLY_DESCRIPTION = `Limits this partner's licence managers to
      <b>fixed</b> licences, which run between a start and an end date.
      <br /><br />
      When off, <b>floating</b> licences, which instead run for a set duration,
      are allowed as well.`;

  NFR_DEFAULTS_DESCRIPTION = `Prefilled on new <b>NFR</b> licences.<br /><br />
      Only a starting point: licence managers may still change the value before
      creating the licence.`;

  ALLOW_SAP_DESCRIPTION = `Allows this partner's licence managers to issue
      <b>SAP</b> licences.<br /><br />
      SAP is opt-in: while this is off, they may only issue <b>Standard</b>
      licences and the edition selector stays hidden from the licence form.`;

  FIELDS: InputField[] = [
    {
      name: "name",
      type: "string",
      label: "Partner",
      required: true,
    },
    {
      name: "poc_licences",
      type: "integer",
      label: "PoC per Customer",
      required: true,
      value: 5,
      validate: (field: InputField) => Number(field.value) > 0,
    },
    {
      name: "trial_licences",
      type: "integer",
      label: "Trials per Customer",
      required: true,
      value: 5,
      validate: (field: InputField) => Number(field.value) > 0,
    },
    {
      name: "country",
      type: "string",
      label: "Country",
      enum: countries.map(c => ({ label: c.name, value: c.code })),
      noSelectionMessage: "Select a country",
    },
    {
      name: "contact",
      type: "string",
      label: "Contact Name",
    },
    {
      name: "email",
      type: "string",
      label: "Contact Email",
      validate: (field: InputField) =>
        field.value
          ? /^\S.*@.*\..*\S$/.test(field.value) || "Incorrect email format"
          : true,
    },
    {
      name: "description",
      type: "string",
      label: "Description",
    },
    // Licensing policy fields
    {
      name: "policy_restrict_tags",
      type: "boolean",
      label: "Restrict to Production & NFR tags only",
      description: this.RESTRICT_TAGS_DESCRIPTION,
      value: false,
    },
    {
      name: "policy_fixed_type_only",
      type: "boolean",
      label: "Fixed licence type only",
      description: this.FIXED_TYPE_ONLY_DESCRIPTION,
      value: false,
    },
    {
      name: "policy_nfr_default_replicas",
      type: "integer",
      label: "NFR default VM replicas",
      description: this.NFR_DEFAULTS_DESCRIPTION,
      value: 0,
    },
    {
      name: "policy_nfr_default_migrations",
      type: "integer",
      label: "NFR default VM migrations",
      description: this.NFR_DEFAULTS_DESCRIPTION,
      value: 5,
      validate: (field: InputField) => Number(field.value) >= 0,
    },
    {
      name: "policy_allow_sap_licences",
      type: "boolean",
      label: "Allow SAP licences",
      description: this.ALLOW_SAP_DESCRIPTION,
      value: false,
    },
  ];

  initPolicyFields(fields: InputField[], policy: LicensingPolicy | null) {
    return fields.map(f => {
      if (f.name === "policy_restrict_tags") {
        return { ...f, value: !!policy?.allowed_tags?.length };
      }
      if (f.name === "policy_fixed_type_only") {
        return {
          ...f,
          value: policy?.allowed_licence_types?.includes("fixed") ?? false,
        };
      }
      if (f.name === "policy_nfr_default_replicas") {
        return {
          ...f,
          value: policy?.tag_policies?.["NFR"]?.defaults?.replicas ?? 0,
        };
      }
      if (f.name === "policy_nfr_default_migrations") {
        return {
          ...f,
          value: policy?.tag_policies?.["NFR"]?.defaults?.migrations ?? 5,
        };
      }
      if (f.name === "policy_allow_sap_licences") {
        return { ...f, value: isSapAllowedByPolicy(policy) };
      }
      return f;
    });
  }

  buildPolicy(fields: InputField[]): LicensingPolicy | null {
    const getValue = (name: string): any =>
      fields.find(f => f.name === name)?.value;

    const restrictTags: boolean = getValue("policy_restrict_tags");
    const fixedTypeOnly: boolean = getValue("policy_fixed_type_only");
    const nfrDefaultReplicas: number = Number(
      getValue("policy_nfr_default_replicas"),
    );
    const nfrDefaultMigrations: number = Number(
      getValue("policy_nfr_default_migrations"),
    );
    const allowSapLicences: boolean = getValue("policy_allow_sap_licences");

    if (!restrictTags && !fixedTypeOnly && !allowSapLicences) {
      return null;
    }

    const policy: LicensingPolicy = {};

    if (restrictTags) {
      policy.allowed_tags = ["Production", "NFR"];
      policy.tag_policies = {
        NFR: {
          defaults: {
            replicas: nfrDefaultReplicas,
            migrations: nfrDefaultMigrations,
          },
          enforced: false,
        },
      };
    }

    if (fixedTypeOnly) {
      policy.allowed_licence_types = ["fixed"];
    }

    // NOTE: only spelled out when SAP is granted; the absence of the field is
    // what limits every other partner to standard licences:
    if (allowSapLicences) {
      policy.allowed_licence_versions = [...LICENCE_VERSIONS];
    }

    return policy;
  }

  buildData(fields: InputField[]): PartnerInput {
    const getValue = (name: string): any => {
      const field = fields.find(f => f.name === name);
      if (!field) {
        throw new Error(`Can't find field named '${name}'`);
      }
      return field.value;
    };

    return {
      name: getValue("name"),
      poc_licences: getValue("poc_licences"),
      trial_licences: getValue("trial_licences"),
      country: getValue("country"),
      contact: getValue("contact"),
      email: getValue("email"),
      description: getValue("description"),
      licensing_policy: this.buildPolicy(fields),
    };
  }
}

export default new InputFields();
